import moment from 'moment';

import { OptionType } from '../AutoComplete';
import { FormFieldTypes } from '../Form';
import { FormSchema } from '../Form/types';

import {
  DEMO_REQUEST_TYPE,
  DEMO_TIME_OPTIONS,
  EMAIL_LIST_ERROR_TEXT,
  EMAIL_LIST_PATTERN,
  FEEDBACK_REQUEST_TYPE,
} from './constants';

export const buildTaskRequestFormSchema = (): FormSchema => ({
  properties: {
    report_title: {
      type: FormFieldTypes.string,
      title: 'Title',
      placeholder: 'Short summary of the request',
      maxLength: 120,
    },
    report_description: {
      type: FormFieldTypes.string,
      title: 'Description',
      placeholder: 'What happened, and what did you expect to happen?',
      multiline: true,
      rows: 4,
    },
    error_message: {
      type: FormFieldTypes.string,
      title: 'Error Message',
      placeholder: 'Paste any on-screen error message (optional)',
      multiline: true,
      rows: 2,
    },
    additional_context: {
      type: FormFieldTypes.string,
      title: 'Additional Context',
      placeholder: 'Anything else that helps us reproduce it (optional)',
      multiline: true,
      rows: 2,
    },
  },
  required: ['report_title', 'report_description'],
});

export const buildFeedbackFormSchema = (): FormSchema => ({
  properties: {
    feedback: {
      type: FormFieldTypes.string,
      title: 'Feedback',
      placeholder: 'What works well, and what could we improve?',
      multiline: true,
      rows: 6,
      maxLength: 2000,
    },
  },
  required: ['feedback'],
});

export const buildDemoRequestFormSchema = (): FormSchema => ({
  properties: {
    date_range: {
      type: FormFieldTypes.dateRange,
      title: 'Preferred Dates',
      placeholder: 'Pick a date range that suits you',
      minDate: moment().format('YYYY-MM-DD'),
    },
    time_selection: {
      type: FormFieldTypes.select,
      title: 'Preferred Time',
      options: DEMO_TIME_OPTIONS,
      optionsLoading: false,
      placeholder: 'Select a time',
    },
    topic: {
      type: FormFieldTypes.string,
      title: 'Topic',
      placeholder: 'What would you like the demo to cover?',
      maxLength: 120,
    },
    emails: {
      type: FormFieldTypes.string,
      title: 'Additional Attendees',
      placeholder: 'colleague@company.com, cfo@company.com (optional)',
      pattern: EMAIL_LIST_PATTERN,
      patternErrorText: EMAIL_LIST_ERROR_TEXT,
    },
  },
  required: ['date_range', 'time_selection', 'topic'],
});

export const getOptionValue = (
  option: OptionType | string | number | null | undefined,
): string =>
  typeof option === 'object' && option !== null
    ? String(option.value)
    : String(option ?? '');

export const isEmailedRequestType = (requestType: string): boolean =>
  requestType === FEEDBACK_REQUEST_TYPE || requestType === DEMO_REQUEST_TYPE;

// The picker already formats both ends as YYYY-MM-DD, the backend only stores
// the range as text for the demo request email.
export const formatDemoDateRange = (
  dateRange: [string | null, string | null],
): string => `${dateRange[0]} - ${dateRange[1]}`;

export const parseEmails = (emails: string): string[] =>
  emails
    .split(',')
    .map((email) => email.trim())
    .filter(Boolean);
